import React, { useState, useRef, useEffect } from 'react';
import {
  Calendar as CalendarIcon,
  CaretLeft,
  CaretRight,
  CaretDown,
} from '@phosphor-icons/react';

interface CalendarDatePickerProps {
  value?: string; // YYYY-MM-DD
  onChange: (dateStr: string) => void;
  minDate?: string; // YYYY-MM-DD
  maxDate?: string; // YYYY-MM-DD
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** Convert YYYY-MM-DD to DD/MM/YYYY for user-friendly display */
export const formatDisplayDate = (isoStr?: string | null): string => {
  if (!isoStr || !isoStr.trim() || isoStr === 'N/A') return '—';
  const parts = isoStr.split('-');
  if (parts.length === 3) {
    const [y, m, d] = parts;
    return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
  }
  return isoStr;
};

export const CalendarDatePicker: React.FC<CalendarDatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  label,
  placeholder = 'DD/MM/YYYY',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Parse initial view year and month
  const initialDate = value ? new Date(value + 'T00:00:00') : new Date();
  const [viewYear, setViewYear] = useState<number>(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(initialDate.getMonth()); // 0-11

  // Update view when value changes
  useEffect(() => {
    if (value) {
      const d = new Date(value + 'T00:00:00');
      if (!isNaN(d.getTime())) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
    }
  }, [value]);

  // Click outside listener
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Derive min/max years
  const minYear = minDate ? parseInt(minDate.split('-')[0], 10) : 2005;
  const maxYear = maxDate ? parseInt(maxDate.split('-')[0], 10) : new Date().getFullYear();

  const yearOptions: number[] = [];
  for (let y = minYear; y <= maxYear; y++) {
    yearOptions.push(y);
  }

  // Navigation handlers
  const handlePrevMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (viewMonth === 0) {
      if (viewYear > minYear) {
        setViewYear(viewYear - 1);
        setViewMonth(11);
      }
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const handleNextMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (viewMonth === 11) {
      if (viewYear < maxYear) {
        setViewYear(viewYear + 1);
        setViewMonth(0);
      }
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  // Build days matrix for viewMonth and viewYear
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay(); // 0 = Sunday
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const isDateDisabled = (day: number): boolean => {
    const dStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (minDate && dStr < minDate) return true;
    if (maxDate && dStr > maxDate) return true;
    return false;
  };

  const isDateSelected = (day: number): boolean => {
    if (!value) return false;
    const dStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return value === dStr;
  };

  const isDateToday = (day: number): boolean => {
    const today = new Date();
    return (
      today.getFullYear() === viewYear &&
      today.getMonth() === viewMonth &&
      today.getDate() === day
    );
  };

  const handleSelectDay = (day: number) => {
    if (isDateDisabled(day)) return;
    const dStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onChange(dStr);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {label && (
        <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block mb-1">
          {label}
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2 bg-slate-50 border rounded-xl text-xs font-semibold transition-all ${
          disabled
            ? 'opacity-50 cursor-not-allowed border-slate-200 text-slate-400'
            : isOpen
            ? 'border-accent ring-2 ring-accent/15 bg-white text-slate-900 shadow-xs'
            : 'border-slate-200 hover:border-slate-300 text-slate-800 hover:bg-white'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <CalendarIcon
            size={14}
            weight="bold"
            className={value ? 'text-accent' : 'text-slate-400'}
          />
          <span className={value ? 'font-mono text-slate-900' : 'text-slate-400 font-normal'}>
            {value ? formatDisplayDate(value) : placeholder}
          </span>
        </div>
        <CaretDown size={12} weight="bold" className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180 text-accent' : ''}`} />
      </button>

      {/* Popover Calendar */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1.5 z-50 w-72 bg-white rounded-2xl shadow-xl border border-slate-200/80 p-3.5 animate-in fade-in zoom-in-95 duration-150 font-sans">
          
          {/* Header Navigation with Month and Year Jump Selectors */}
          <div className="flex items-center justify-between gap-1 mb-3 pb-2.5 border-b border-slate-100">
            <button
              type="button"
              onClick={handlePrevMonth}
              disabled={viewYear <= minYear && viewMonth === 0}
              className="p-1 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-20 disabled:pointer-events-none transition-colors"
              title="Previous Month"
            >
              <CaretLeft size={14} weight="bold" />
            </button>

            <div className="flex items-center gap-1.5">
              {/* Month Selector */}
              <select
                value={viewMonth}
                onChange={(e) => setViewMonth(parseInt(e.target.value, 10))}
                className="text-xs font-bold text-slate-800 bg-transparent py-0.5 px-1 rounded-md hover:bg-slate-100 focus:outline-none cursor-pointer"
              >
                {MONTH_NAMES.map((m, idx) => (
                  <option key={m} value={idx}>
                    {m}
                  </option>
                ))}
              </select>

              {/* Year Selector */}
              <select
                value={viewYear}
                onChange={(e) => setViewYear(parseInt(e.target.value, 10))}
                className="text-xs font-bold text-slate-800 bg-transparent py-0.5 px-1 rounded-md hover:bg-slate-100 focus:outline-none cursor-pointer"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={handleNextMonth}
              disabled={viewYear >= maxYear && viewMonth === 11}
              className="p-1 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-20 disabled:pointer-events-none transition-colors"
              title="Next Month"
            >
              <CaretRight size={14} weight="bold" />
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 text-center mb-1">
            {DAYS_OF_WEEK.map((d) => (
              <span key={d} className="text-[10px] font-bold text-slate-400 uppercase">
                {d}
              </span>
            ))}
          </div>

          {/* Day Grid */}
          <div className="grid grid-cols-7 gap-1 text-center">
            {/* Blank offset days */}
            {Array.from({ length: firstDayOfWeek }).map((_, i) => (
              <div key={`blank-${i}`} className="w-8 h-8" />
            ))}

            {/* Actual Month Days */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const disabledDay = isDateDisabled(day);
              const selected = isDateSelected(day);
              const today = isDateToday(day);

              return (
                <button
                  key={`day-${day}`}
                  type="button"
                  disabled={disabledDay}
                  onClick={() => handleSelectDay(day)}
                  className={`w-8 h-8 rounded-xl text-xs font-semibold flex items-center justify-center transition-all ${
                    selected
                      ? 'bg-accent text-white font-bold shadow-sm'
                      : disabledDay
                      ? 'text-slate-300 opacity-25 cursor-not-allowed bg-slate-50/50'
                      : today
                      ? 'text-accent font-bold border border-accent/40 bg-accent/5 hover:bg-accent/15'
                      : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Footer bounds indicator */}
          <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-[9px] text-slate-400 font-medium">
            <span>Historical Bounds:</span>
            <span className="font-mono font-semibold text-slate-600">
              {formatDisplayDate(minDate)} → {formatDisplayDate(maxDate)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
